import { SparkleIcon } from '@phosphor-icons/react';
import type { CSSProperties } from 'react';
import {
  appendMemberSpotlightListItem,
  memberSpotlightSnapshotToRecord,
  parseMemberSpotlightSnapshot,
  removeMemberSpotlightListItem,
  setMemberSpotlightListItem,
  type MemberSpotlightListKey,
  type MemberSpotlightSnapshot,
} from '@act/shared/data-transfer-objects';
import SectionReveal from './SectionReveal';
import SectionBanner from './SectionBanner';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { foreground, muted, accent, border, body, display } from './tokens';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { MemberSpotlightBulletList } from './MemberSpotlightBulletList';
import { MemberSpotlightEditableText } from './MemberSpotlightEditableText';
import { MemberSpotlightIcon } from './MemberSpotlightIcon';

const eyebrowStyle: CSSProperties = {
  color: accent,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: '0.18em',
};

const subHeadingStyle: CSSProperties = {
  fontFamily: display,
  fontWeight: 600,
  fontSize: 20,
  color: foreground,
  margin: '0 0 8px',
  lineHeight: 1.25,
  letterSpacing: '-0.01em',
  display: 'block',
};

const nameStyle: CSSProperties = {
  fontFamily: display,
  fontWeight: 600,
  fontSize: 30,
  color: foreground,
  margin: 0,
  lineHeight: 1.1,
  letterSpacing: '-0.01em',
  display: 'block',
};

const summaryStyle: CSSProperties = {
  fontFamily: body,
  fontSize: 15,
  color: muted,
  margin: 0,
  lineHeight: 1.6,
  display: 'block',
};

type LabelKey = keyof MemberSpotlightSnapshot['labels'];

/**
 * Hosted Member Spotlight section. In the builder, all visible copy is
 * inline-editable and persisted on the section snapshot.
 * @param props - Resolved snapshot and optional authoring handlers.
 */
const MemberStory = ({
  data,
  authoringEditable = false,
  onDataSnapshotChange,
  onDataSnapshotBlur,
}: ReportHostedSectionProps) => {
  const snapshot = parseMemberSpotlightSnapshot(data);

  const emitChange = (next: MemberSpotlightSnapshot) => {
    onDataSnapshotChange?.(memberSpotlightSnapshotToRecord(next));
  };

  const emitBlur = (next: MemberSpotlightSnapshot) => {
    onDataSnapshotBlur?.(memberSpotlightSnapshotToRecord(next));
  };

  const emitLabel = (key: LabelKey, value: string, persistNow: boolean) => {
    const next = {
      ...snapshot,
      labels: { ...snapshot.labels, [key]: value },
    };
    if (persistNow) {
      emitBlur(next);
    } else {
      emitChange(next);
    }
  };

  const updateListItem = (
    key: MemberSpotlightListKey,
    index: number,
    value: string,
    persistNow: boolean,
  ) => {
    const next = setMemberSpotlightListItem(snapshot, key, index, value);
    if (persistNow) {
      emitBlur(next);
    } else {
      emitChange(next);
    }
  };

  const listHandlers = (key: MemberSpotlightListKey) => ({
    onItemChange: (index: number, value: string) =>
      updateListItem(key, index, value, false),
    onItemBlur: (index: number, value: string) =>
      updateListItem(key, index, value, true),
    onAdd: () => emitBlur(appendMemberSpotlightListItem(snapshot, key)),
    onRemove: (index: number) =>
      emitBlur(removeMemberSpotlightListItem(snapshot, key, index)),
  });

  return (
    <HostedReportSectionLayout
      sectionDomId="member-story"
      className="report-hosted-section-body"
      style={{ background: 'var(--reema-sand-100)', fontFamily: body }}
      innerClassName="max-w-6xl mx-auto"
      banner={
        <SectionBanner
          title="Member Spotlight"
          pb={40}
          icon={
            <SparkleIcon
              className="banner__icon"
              aria-hidden="true"
              style={{ color: accent }}
            />
          }
        />
      }
    >
      <SectionReveal delay={0.1}>
        <div className="member-spotlight-layout grid md:grid-cols-[minmax(0,400px)_minmax(0,1fr)] gap-8 md:gap-10 items-start">
          <div
            className="member-spotlight-profile-card rounded-2xl p-5 shadow-sm"
            style={{ background: '#fff', border: `1px solid ${border}` }}
          >
            <div className="flex items-center gap-3">
              <MemberSpotlightIcon
                alt={snapshot.name || 'Member'}
                className="w-14 h-14 md:w-16 md:h-16 shrink-0"
              />
              <MemberSpotlightEditableText
                as="h3"
                value={snapshot.name}
                style={nameStyle}
                editable={authoringEditable}
                onChange={value => emitChange({ ...snapshot, name: value })}
                onBlur={value => emitBlur({ ...snapshot, name: value })}
              />
            </div>

            <hr
              className="my-5"
              style={{ border: 0, borderTop: `1px solid ${border}` }}
            />

            <div className="mb-6">
              <MemberSpotlightEditableText
                as="p"
                value={snapshot.labels.needs}
                className="uppercase mb-3"
                style={eyebrowStyle}
                editable={authoringEditable}
                onChange={value => emitLabel('needs', value, false)}
                onBlur={value => emitLabel('needs', value, true)}
              />
              <MemberSpotlightBulletList
                items={snapshot.needs}
                listKey="needs"
                editable={authoringEditable}
                {...listHandlers('needs')}
              />
            </div>

            <div>
              <MemberSpotlightEditableText
                as="p"
                value={snapshot.labels.ongoingPriorities}
                className="uppercase mb-3"
                style={eyebrowStyle}
                editable={authoringEditable}
                onChange={value =>
                  emitLabel('ongoingPriorities', value, false)
                }
                onBlur={value => emitLabel('ongoingPriorities', value, true)}
              />
              <MemberSpotlightBulletList
                items={snapshot.ongoingPriorities}
                listKey="ongoingPriorities"
                editable={authoringEditable}
                {...listHandlers('ongoingPriorities')}
              />
            </div>
          </div>

          <div className="member-spotlight-narrative flex flex-col gap-5">
            <div>
              <MemberSpotlightEditableText
                as="h3"
                value={snapshot.labels.summary}
                style={subHeadingStyle}
                editable={authoringEditable}
                onChange={value => emitLabel('summary', value, false)}
                onBlur={value => emitLabel('summary', value, true)}
              />
              <MemberSpotlightEditableText
                as="p"
                value={snapshot.summary}
                style={summaryStyle}
                editable={authoringEditable}
                onChange={value => emitChange({ ...snapshot, summary: value })}
                onBlur={value => emitBlur({ ...snapshot, summary: value })}
              />
            </div>

            <div>
              <MemberSpotlightEditableText
                as="h3"
                value={snapshot.labels.challenge}
                style={subHeadingStyle}
                editable={authoringEditable}
                onChange={value => emitLabel('challenge', value, false)}
                onBlur={value => emitLabel('challenge', value, true)}
              />
              <MemberSpotlightBulletList
                items={snapshot.challenge}
                listKey="challenge"
                editable={authoringEditable}
                {...listHandlers('challenge')}
              />
            </div>

            <div>
              <MemberSpotlightEditableText
                as="h3"
                value={snapshot.labels.howReemaHelped}
                style={subHeadingStyle}
                editable={authoringEditable}
                onChange={value => emitLabel('howReemaHelped', value, false)}
                onBlur={value => emitLabel('howReemaHelped', value, true)}
              />
              <MemberSpotlightBulletList
                items={snapshot.howReemaHelped}
                listKey="howReemaHelped"
                editable={authoringEditable}
                {...listHandlers('howReemaHelped')}
              />
            </div>

            <div>
              <MemberSpotlightEditableText
                as="h3"
                value={snapshot.labels.outcomes}
                style={subHeadingStyle}
                editable={authoringEditable}
                onChange={value => emitLabel('outcomes', value, false)}
                onBlur={value => emitLabel('outcomes', value, true)}
              />
              <MemberSpotlightBulletList
                items={snapshot.outcomes}
                listKey="outcomes"
                editable={authoringEditable}
                {...listHandlers('outcomes')}
              />
            </div>
          </div>
        </div>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default MemberStory;
